/**
 * The EnvironmentManager
 */
import * as React from "react"
import { ThemeProvider } from "@oui/system/styled"
import { Theme } from "@oui/system/theme"
import { en_US, LocaleProvider, LocaleContext } from "../Locale"
import { LayerManager } from "../Layer"
import { FocusManager } from "../FocusTrap"
import EnvironmentContext from "./EnvironmentContext"
import { Environment, defaultEnvironment } from "./Environment"
import { mergeDeepRight } from "ramda"
import ToastProvider from "../ToastNotification/ToastProvider"

type DefaultProps = typeof defaultProps
type Props = Readonly<
  {
    /**
     * Oui Core theme.
     */
    theme: Theme
  } & Pick<React.HTMLAttributes<HTMLElement>, "children"> &
    DefaultProps
>

const defaultProps = {
  /**
   * Environment.
   * @default defaultEnvironment
   */
  environment: (defaultEnvironment as any) as Partial<Environment>,
  /**
   * Localization strings.
   * @default en_US
   */
  locale: en_US as React.ContextType<typeof LocaleContext>
}

const EnvironmentManager = ({
  children,
  environment,
  locale,
  theme
}: Props) => {
  const env = mergeDeepRight(
    defaultProps.environment,
    environment!
  ) as Environment
  return (
    <EnvironmentContext.Provider value={env}>
      <ThemeProvider theme={theme}>
        <LocaleProvider locale={locale}>
          <LayerManager>
            <FocusManager>
              <ToastProvider>{children}</ToastProvider>
            </FocusManager>
          </LayerManager>
        </LocaleProvider>
      </ThemeProvider>
    </EnvironmentContext.Provider>
  )
}
EnvironmentManager.defaultProps = defaultProps

export default React.memo(EnvironmentManager)
